import type { ReactNode } from 'react';
import { FiAlertCircle, FiCheck } from 'react-icons/fi';
import { cn } from '@/shared/lib';
import { styles } from '@/shared/app-stepper-form/styles';
import type { StepState } from '@/shared/app-stepper-form/hooks/use-stepper';
import type { StepDefinition } from '@/shared/app-stepper-form/types';

/**
 * How one step reads, as a single word.
 *
 * Derived once and used for BOTH the visible state line and the accessible
 * suffix on the button, so the two can never disagree. The previous version
 * built the suffix inline and the visible state did not exist at all, which
 * meant the rail's only state signal was colour — the thing the colour rule
 * forbids.
 */
type StepStatus = 'complete' | 'current' | 'blocked' | 'upcoming';

function statusOf(index: number, activeIndex: number, blocked: boolean): StepStatus {
  if (blocked) return 'blocked';
  if (index < activeIndex) return 'complete';
  if (index === activeIndex) return 'current';
  return 'upcoming';
}

const STATUS_TEXT: Record<StepStatus, string> = {
  complete: 'Complete',
  current: 'In progress',
  blocked: 'Needs attention',
  upcoming: 'Not started',
};

const MARKER_CLASS: Record<StepStatus, string> = {
  complete: styles.railMarkerComplete,
  current: styles.railMarkerCurrent,
  blocked: styles.railMarkerBlocked,
  upcoming: styles.railMarkerPending,
};

const LABEL_CLASS: Record<StepStatus, string> = {
  complete: styles.railLabelIdle,
  current: styles.railLabelCurrent,
  blocked: styles.railLabelCurrent,
  upcoming: styles.railLabelIdle,
};

const STATE_CLASS: Record<StepStatus, string> = {
  complete: styles.railStateComplete,
  current: styles.railStateCurrent,
  blocked: styles.railStateBlocked,
  upcoming: styles.railStateIdle,
};

/**
 * The marker glyph.
 *
 * A tick for complete, a warning for blocked, the step number otherwise. The
 * glyph is decorative — `aria-hidden` — because the state is already in the
 * button's accessible name and in the visible state line. Announcing it a third
 * time would make the rail tedious to traverse.
 */
function MarkerGlyph({ status, index }: { status: StepStatus; index: number }): ReactNode {
  if (status === 'complete') return <FiCheck aria-hidden="true" className="size-3.5" />;
  if (status === 'blocked') return <FiAlertCircle aria-hidden="true" className="size-3.5" />;
  return <span aria-hidden="true">{index + 1}</span>;
}

/**
 * The step rail.
 *
 * State is conveyed by ICON AND TEXT, never colour alone — a blocked step reads
 * "Needs attention" and a completed one reads "Complete", so the rail is fully
 * usable without colour vision.
 *
 * A step is jumpable only if already reached. Later steps stay locked so the rail
 * cannot be used to skip past validation.
 *
 * The accessible name carries the state as a suffix ("Details — Complete") so a
 * screen-reader user gets the same information a sighted user reads off the row,
 * without having to infer it from the marker.
 */
export function StepRail({
  steps,
  states,
  activeIndex,
  furthestReached,
  onJump,
  className,
}: {
  steps: ReadonlyArray<StepDefinition>;
  states: ReadonlyArray<StepState>;
  activeIndex: number;
  furthestReached: number;
  onJump: (index: number) => void;
  /**
   * Overrides the list's own layout classes.
   *
   * The rail is rendered in two places: the desktop aside, where it sits inside
   * the tinted column, and the mobile sheet, where it sits inside the sheet body.
   * Both are plain columns, so the default is `styles.railList` — the list's own
   * stacking, with none of the column's chrome.
   *
   * It must NOT default to `styles.rail`. That token describes the COLUMN, and
   * the column is the parent that renders this list; applying it here re-applied
   * `lg:w-72` and `p-3` to a child of a box that already had them, so the list
   * overflowed the rail and painted its tint and right border over the panel.
   */
  className?: string;
}): ReactNode {
  return (
    <ol className={className ?? styles.railList} aria-label="Progress">
      {steps.map((step, index) => {
        const status = statusOf(index, activeIndex, states[index]?.blocked === true);
        const reachable = status === 'complete' || status === 'current' || index <= furthestReached;

        return (
          <li key={step.id} className={cn(styles.railItem, 'relative')}>
            {index < steps.length - 1 ? (
              <span className={styles.railConnector} aria-hidden="true" />
            ) : null}
            <button
              type="button"
              className={cn(styles.railButton, status === 'current' && styles.railButtonCurrent)}
              aria-current={status === 'current' ? 'step' : undefined}
              disabled={!reachable}
              onClick={() => {
                onJump(index);
              }}
            >
              <span className={cn(styles.railMarker, MARKER_CLASS[status])}>
                <MarkerGlyph status={status} index={index} />
              </span>
              <span className={styles.railText}>
                {/*
                  The visible label is just the step's name — the state is on the
                  line below it. The accessible name carries BOTH, because a
                  screen-reader user traversing the rail should hear the state
                  without having to leave the button.

                  The visible state line is `aria-hidden` and the `sr-only` span
                  supplies the same words to the accessible name. Without that
                  split the name reads "Details In progress — In progress".
                */}
                <span className={cn(styles.railLabel, LABEL_CLASS[status])}>{step.label}</span>
                <span
                  className={cn(styles.railState, STATE_CLASS[status])}
                  aria-hidden="true"
                >
                  {STATUS_TEXT[status]}
                </span>
                <span className="sr-only">{` — ${STATUS_TEXT[status]}`}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
